import * as React from "react";
import { useTranslation } from "react-i18next";

import { Generic } from "src/@types/Category";

import agent from "src/helpers/apiAgent";
import { successHandler, errorHandler } from "src/helpers/responseHandler";

import { PROTECTED_ROUTES } from "src/constants/Navigation";

import useRoutes from "src/hooks/useRoutes";
import useCourse from "src/hooks/useCourse";
import useNewAssignment from "src/hooks/useNewAssignment";

import { trackAction } from "src/helpers/tracking";
import { hasRealContent } from "src/helpers/common";
import useResolvers from "src/hooks/useResolvers";
import { QUESTION_TYPE } from "src/@types/Exam";

interface MatrixConfiguration {
  rows?: Array<{ value: string }>;
  columns?: Array<{ value: string }>;
  matrix?: Record<string, Generic>;
  tabs?: Array<{ title: string; content: string }>;
  allowMultiSelect?: boolean;
}

interface FillInTheBlankConfiguration {
  blanks?: Array<{ value: string; id: string; correctAnswer: Array<string> }>;
  tabs?: Array<{ title: string; content: string }>;
  options?: Array<Generic>;
  ordered?: boolean;
}

interface FillInTheBlankFromListConfiguration {
  blanks?: Array<{
    value: string;
    id: string;
    correctAnswer: Array<string>;
    options: Array<{
      id: string;
      value: string;
      blankPosition: number;
    }>;
  }>;
  tabs?: Array<{ title: string; content: string }>;
  ordered?: boolean;
}

interface BowTieConfiguration {
  tabs?: Array<{ title: string; content: string }>;
  actions?: Array<{ value: string; id: string }>;
  parameters?: Array<{ value: string; id: string }>;
  conditions?: Array<{ value: string; id: string }>;
  correctActions?: Array<string>;
  correctParameters?: Array<string>;
  correctConditions?: Array<string>;
}

interface FindingsConfiguration {
  tabs?: Array<{ title: string; content: string }>;
  findings?: Array<{ value: string; id: string }>;
  correctFindings?: Array<string>;
}

export interface HighlightedConfiguration {
  tabs?: Array<{ title: string; content: string }>;
  tableData: string[][]; // 2D array for table rows and columns
  tableHighlightedPhrases: TableHighlightedPhrase[];
  highlightedText: string; // HTML content with {{highlighted}} phrases
  highlightedPhrases: HighlightedPhrase[];
  tableHeaders: string[];
  maxSelections?: number;
  scoringMode?: "plus_minus" | "zero_one";
}

export interface CaseStudyConfiguration {
  tabs?: Array<{
    title: string;
    content: string;
    displayFrom: number;
    displayTill: number;
  }>;
  questions: Array<QuestionData>;
}

export interface TableHighlightedPhrase {
  id: string;
  text: string;
  fullMatch: string; // e.g. "{{...}}"
  isCorrect: boolean;
  location: string; // "Row 1, Col 2"
  rowIndex: number;
  colIndex: number;
}

export interface HighlightedPhrase {
  id: string;
  text: string;
  fullMatch: string; // e.g. "{{...}}"
  isCorrect: boolean;
}

type QuestionConfiguration =
  | MatrixConfiguration
  | FillInTheBlankConfiguration
  | BowTieConfiguration
  | FindingsConfiguration
  | FillInTheBlankFromListConfiguration
  | HighlightedConfiguration
  | CaseStudyConfiguration
  | Record<string, Generic>;

export interface QuestionData {
  questionType: number;
  courseId: string;
  questionCategory: number;
  questionText: string;
  score: number;
  configuration: QuestionConfiguration;
  difficulty: number;
  options: Generic[];
  tags: string[];
  gradeId: string;
  reasoning: string;
}

// Set defaults for reuse
export const DEFAULTS = {
  loading: false,
  questionData: {
    questionType: 1,
    courseId: "",
    questionCategory: 1,
    questionText: "",
    score: 0,
    configuration: {},
    difficulty: 1,
    options: [],
    tags: [],
    gradeId: "",
    reasoning: "",
  } as QuestionData,
  updateId: "",
  subject: { id: "", name: "", courseId: "", courseName: "" },
  onCancel: () => {},
  resetQuestionData: () => {},
  onSubmitQuestion: async () => {},
  setSubject: (subject: Generic) => {},
  addQuestionTags: (value: string) => {},
  setQuestionData: (data: Generic) => {},
  removeQuestionTag: (value: string) => {},
  editQuestion: (
    data: Generic,
    subject: Generic,
    isGPT?: boolean,
    idx?: number
  ) => {},
};

const QuestionContext = React.createContext(DEFAULTS);

const QuestionProvider: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
}) => {
  const { t } = useTranslation();
  const { activeGrade } = useCourse();
  const { navigateTo } = useRoutes();
  const { setRefetch } = useNewAssignment();
  const { QuestionTypeResolver } = useResolvers();

  const [updateId, setUpdateId] = React.useState(DEFAULTS?.updateId);
  const [loading, setLoading] = React.useState(DEFAULTS.loading);
  const [subject, setSubject] = React.useState(DEFAULTS?.subject);
  const [questionData, setQuestionData] = React.useState(DEFAULTS.questionData);

  React.useEffect(() => {
    if (activeGrade?.id) {
      setQuestionData((prev) => ({
        ...prev,
        gradeId: activeGrade?.id,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGrade]);

  const resetQuestionData = () => {
    // persisting gradeId
    setQuestionData({ ...DEFAULTS?.questionData, gradeId: activeGrade?.id });
    setUpdateId(DEFAULTS?.updateId);
  };

  const editQuestion = (data, subject, isGPT = false, idx = -1) => {
    const extractedData = {};
    Object.keys(DEFAULTS?.questionData).forEach(
      (key) => (extractedData[key] = data[key] ?? DEFAULTS.questionData[key])
    );
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setQuestionData(extractedData);
    setSubject(subject);
    setUpdateId(data?.id);
    navigateTo(PROTECTED_ROUTES.ADD_QUESTIONS, {
      state: { subject, isGPT, questionIndex: idx },
    });
  };

  const addQuestionTags = (val: string) => {
    const index = questionData?.tags.indexOf(val, 0);
    if (index > -1) {
      const updated = [...questionData?.tags];
      updated.splice(index, 1);
      setQuestionData((prev) => ({ ...prev, tags: updated }));
    } else {
      setQuestionData((prev) => ({
        ...prev,
        tags: [...questionData?.tags, val],
      }));
    }
  };

  const removeQuestionTag = (val: string) => {
    const index = questionData?.tags.indexOf(val, 0);
    if (index > -1) {
      const updated = [...questionData?.tags];
      updated.splice(index, 1);
      setQuestionData((prev) => ({ ...prev, tags: updated }));
    }
  };

  // Helper function to check if questionText has actual content
  const hasActualContent = (htmlString: string) => {
    if (!htmlString) return false;

    // Remove HTML tags and trim whitespace
    const textContent = htmlString
      .replace(/<[^>]*>/g, "") // Remove all HTML tags
      .replace(/&nbsp;/g, " ") // Replace &nbsp; with space
      .trim(); // Remove leading/trailing whitespace

    return textContent.length > 0;
  };

  // For FITB validation
  const isBlankInvalid = (str) => {
    // Find all {{...}} placeholders
    const matches = str.match(/{{\s*([^}]*)\s*}}/g);

    // If no placeholders → invalid
    if (!matches) return true;

    // Check if any placeholder is empty
    return matches.some((m) => m.replace(/[{}]/g, "").trim().length === 0);
  };

  const validateCommonFields = () => {
    if (!questionData?.courseId) {
      errorHandler(t("errors.courseSelect"));
      return false;
    }
    if (!questionData?.questionType) {
      errorHandler(t("errors.questionType"));
      return false;
    }
    if (!questionData?.questionCategory) {
      errorHandler(t("errors.questionBank"));
      return false;
    }
    if (!hasActualContent(questionData?.questionText)) {
      errorHandler(t("errors.descriptiveQuestionText"));
      return false;
    }
    if (!questionData?.difficulty) {
      errorHandler(t("errors.questionDifficultyLevel"));
      return false;
    }
    if (!questionData?.gradeId) {
      errorHandler(t("errors.questionGradeInfo"));
      return false;
    }
    if (questionData?.tags?.length === 0) {
      errorHandler(t("errors.questionAITags"));
      return false;
    }
    return true;
  };

  const isMCQOrPictorialValid = (questionData: QuestionData) => {
    if (
      (questionData?.configuration?.tabs &&
        questionData?.configuration?.tabs?.length > 0 &&
        questionData?.configuration?.tabs?.some(
          (tab) => !tab?.content || !hasRealContent(tab.content)
        )) ||
      questionData?.configuration?.tabs?.some((tab) => !tab?.title)
    ) {
      errorHandler(t("errors.tabContent")); // All tabs must have content and title
      return false;
    }
    if (
      questionData?.configuration?.tabs?.some(
        (tab) => tab.content.replace(/<[^>]*>/g, "").trim().length < 3
      ) ||
      questionData?.configuration?.tabs?.some((tab) => tab?.title?.length < 3)
    ) {
      errorHandler(t("errors.tabContentLength")); // Content and Title must be greater than 3 characters
      return false;
    }
    if (questionData?.options?.length < 2) {
      errorHandler(
        t("errors.mcqOptionsTwo", {
          type: QuestionTypeResolver[questionData?.questionType],
        })
      );
      return false;
    }
    // All options must have values
    if (!questionData?.options?.every((op) => op?.value)) {
      errorHandler(
        t("errors.mcqOptionValue", {
          type: QuestionTypeResolver[questionData?.questionType],
        })
      );
      return false;
    }
    // And atlest 1 of the options must be right answer
    if (!questionData?.options?.some((op) => op?.isCorrectAnswer)) {
      errorHandler(
        t("errors.mcqCorrectRequired", {
          type: QuestionTypeResolver[questionData?.questionType],
        })
      );
      return false;
    }
    if (
      questionData?.questionType === QUESTION_TYPE.PICTORIAL &&
      !questionData?.options?.every((op) => op?.name)
    ) {
      errorHandler(
        t("errors.mcqOptionLabel", {
          type: QuestionTypeResolver[questionData?.questionType],
        })
      );
      return false;
    }
    /**
      Removed because of Requirement from Sales team - All options
      can be correct at the same time for SATA use-case
    */
    // if (
    //   questionData?.options?.length ===
    //   questionData?.options?.filter((op) => op?.isCorrectAnswer)?.length
    // ) {
    //   errorHandler(
    //     t("errors.allOptionsCorrect", {
    //       type: QuestionTypeResolver[questionData?.questionType],
    //     })
    //   );
    //   return false;
    // }
    return true;
  };

  const isFillInTheBlankValid = (config: FillInTheBlankConfiguration) => {
    if (
      (config?.tabs &&
        config?.tabs?.length > 0 &&
        config?.tabs?.some(
          (tab) => !tab?.content || !hasRealContent(tab.content)
        )) ||
      config?.tabs?.some((tab) => !tab?.title)
    ) {
      errorHandler(t("errors.tabContent")); // All tabs must have content and title
      return false;
    }
    if (
      config?.tabs?.some(
        (tab) => tab.content.replace(/<[^>]*>/g, "").trim().length < 3
      ) ||
      config?.tabs?.some((tab) => tab?.title?.length < 3)
    ) {
      errorHandler(t("errors.tabContentLength")); // Content and Title must be greater than 3 characters
      return false;
    }
    // if (
    //   !config?.blanks ||
    //   (questionData?.questionType === QUESTION_TYPE.FILL_IN_THE_BLANK &&
    //     config?.blanks?.length < 2)
    // ) {
    //   errorHandler(t("errors.fillInTheBlank")); // At least 2 blanks are required
    //   return false;
    // }
    if (!config?.blanks || config?.blanks?.length < 1) {
      errorHandler(t("errors.fillInTheBlank1")); // At least 1 blank is required
      return false;
    }
    // Check if all blanks have content
    if (!config?.blanks?.every((blank) => blank?.value?.trim())) {
      errorHandler(t("errors.fillInTheBlankEmptyBlanks")); // All blanks must have content
      return false;
    }
    // Check if there are enough options for all blanks
    if (config?.options?.length < config?.blanks?.length) {
      errorHandler(t("errors.fillInTheBlankInsufficientOptions")); // Not enough options for the number of blanks
      return false;
    }
    // options should not be empty and should not contain commas
    if (
      config?.options?.some((o) => !o?.value || o?.value?.includes(",")) ||
      config?.options?.join(",").endsWith(",")
    ) {
      errorHandler(t("errors.fillInTheBlankOptions")); // All options must have content and should not include a comma in them!
      return false;
    }
    if (
      !config?.blanks?.every((b) => b?.correctAnswer) ||
      config?.blanks?.some((b) => !b?.correctAnswer)
    ) {
      errorHandler(t("errors.fillInTheBlankCorrectAnswer")); // All blanks must have correct answers
      return false;
    }
    // blanks must have {{}} in them
    if (
      config?.blanks?.some(
        (b) => !b?.value?.includes("{{") || !b?.value?.includes("}}")
      )
    ) {
      errorHandler(t("errors.fillInTheBlankValue")); // All blanks must have {{ }} in them
      return false;
    }
    // blanks must have {{ value }} in them
    if (config?.blanks?.some((b) => isBlankInvalid(b?.value ?? ""))) {
      errorHandler(t("errors.fillInTheBlankAnswerValue")); // All blanks must have {{ answer }} in them
      return false;
    }
    // # of {{ should be equal to number of }} in each blank
    if (
      config?.blanks?.some(
        (b) =>
          (b?.value?.match(/{{/g) || [])?.length !==
          (b?.value?.match(/}}/g) || [])?.length
      )
    ) {
      errorHandler(t("errors.fillInTheBlankBraces")); // Number of opening and closing braces must be equal in each blank - All blanks must have both opening and closing braces {{ }}
      return false;
    }
    return true;
  };

  const isFillInTheBlankFromListValid = (
    config: FillInTheBlankFromListConfiguration
  ) => {
    if (
      (config?.tabs &&
        config?.tabs?.length > 0 &&
        config?.tabs?.some(
          (tab) => !tab?.content || !hasRealContent(tab.content)
        )) ||
      config?.tabs?.some((tab) => !tab?.title)
    ) {
      errorHandler(t("errors.tabContent")); // All tabs must have content and title
      return false;
    }
    if (
      config?.tabs?.some(
        (tab) => tab.content.replace(/<[^>]*>/g, "").trim().length < 3
      ) ||
      config?.tabs?.some((tab) => tab?.title?.length < 3)
    ) {
      errorHandler(t("errors.tabContentLength")); // Content and Title must be greater than 3 characters
      return false;
    }
    if (!config?.blanks || config?.blanks?.length < 1) {
      errorHandler(t("errors.fillInTheBlank1")); // At least 1 blank is required
      return false;
    }
    // Check if all blanks have content
    if (!config?.blanks?.every((blank) => blank?.value?.trim())) {
      errorHandler(t("errors.fillInTheBlankEmptyBlanks")); // All blanks must have content
      return false;
    }
    // Check if there are enough options for all blanks
    if (
      config?.blanks?.some((b) => b?.options?.length < config?.blanks?.length)
    ) {
      errorHandler(t("errors.fillInTheBlankInsufficientOptions")); // Not enough options for the number of blanks
      return false;
    }
    // options should not be empty and should not contain commas
    if (
      config?.blanks?.some(
        (b) =>
          b?.options?.some((o) => !o?.value || o?.value?.includes(",")) ||
          b?.options?.join(",").endsWith(",")
      )
    ) {
      errorHandler(t("errors.fillInTheBlankOptions")); // All options must have content and should not include a comma in them!
      return false;
    }
    if (
      !config?.blanks?.every((b) => b?.correctAnswer?.length) ||
      config?.blanks?.some((b) => !b?.correctAnswer)
    ) {
      errorHandler(t("errors.fillInTheBlankCorrectAnswer")); // All blanks must have correct answers
      return false;
    }
    // blanks must have {{}} in them
    if (
      config?.blanks?.some(
        (b) => !b?.value?.includes("{{") || !b?.value?.includes("}}")
      )
    ) {
      errorHandler(t("errors.fillInTheBlankValue")); // All blanks must have {{ }} in them
      return false;
    }
    // blanks must have {{ value }} in them
    if (config?.blanks?.some((b) => isBlankInvalid(b?.value ?? ""))) {
      errorHandler(t("errors.fillInTheBlankAnswerValue")); // All blanks must have {{ answer }} in them
      return false;
    }
    // # of {{ should be equal to number of }} in each blank
    if (
      config?.blanks?.some(
        (b) =>
          (b?.value?.match(/{{/g) || [])?.length !==
          (b?.value?.match(/}}/g) || [])?.length
      )
    ) {
      errorHandler(t("errors.fillInTheBlankBraces")); // Number of opening and closing braces must be equal in each blank - All blanks must have both opening and closing braces {{ }}
      return false;
    }
    return true;
  };

  const isBowTieValid = (config: BowTieConfiguration) => {
    if (
      (config?.tabs &&
        config?.tabs?.length > 0 &&
        config?.tabs?.some(
          (tab) => !tab?.content || !hasRealContent(tab.content)
        )) ||
      config?.tabs?.some((tab) => !tab?.title)
    ) {
      errorHandler(t("errors.tabContent")); // All tabs must have content and title
      return false;
    }
    if (
      config?.tabs?.some(
        (tab) => tab.content.replace(/<[^>]*>/g, "").trim().length < 3
      ) ||
      config?.tabs?.some((tab) => tab?.title?.length < 3)
    ) {
      errorHandler(t("errors.tabContentLength")); // Content and Title must be greater than 3 characters
      return false;
    }
    if (
      !config?.actions ||
      config?.actions?.length < 3 ||
      config?.actions?.some((a) => !a?.value) ||
      config?.actions?.join(",").endsWith(",")
    ) {
      errorHandler(t("errors.bowTieActionsRequired")); // Atleast 3 actions are required
      return false;
    }
    if (
      !config?.conditions ||
      config?.conditions?.length < 2 ||
      config?.conditions?.some((o) => !o?.value) ||
      config?.actions?.join(",").endsWith(",")
    ) {
      errorHandler(t("errors.bowTieConditionsRequired")); // Atleast 2 conditions are required
      return false;
    }
    if (
      !config?.parameters ||
      config?.parameters?.length < 3 ||
      config?.parameters?.some((p) => !p?.value) ||
      config?.actions?.join(",").endsWith(",")
    ) {
      errorHandler(t("errors.bowTieParametersRequired")); // Atleast 3 parameters are required
      return false;
    }
    if (
      !config?.correctActions ||
      config?.correctActions?.length < 2 ||
      config?.correctActions?.some((a) => !a)
    ) {
      errorHandler(t("errors.bowTieCorrectActionsRequired")); // Atleast 2 correct actions are required
      return false;
    }
    if (
      !config?.correctConditions ||
      config?.correctConditions?.length < 1 ||
      config?.correctConditions?.some((c) => !c)
    ) {
      errorHandler(t("errors.bowTieCorrectConditionsRequired")); // Atleast 1 correct conditions are required
      return false;
    }
    if (
      !config?.correctParameters ||
      config?.correctParameters?.length < 2 ||
      config?.correctParameters?.some((p) => !p)
    ) {
      errorHandler(t("errors.bowTieCorrectParametersRequired")); // Atleast 2 correct parameters are required
      return false;
    }
    return true;
  };

  const isMatrixValid = (config: MatrixConfiguration) => {
    if (
      (config?.tabs &&
        config?.tabs?.length > 0 &&
        config?.tabs?.some(
          (tab) => !tab?.content || !hasRealContent(tab.content)
        )) ||
      config?.tabs?.some((tab) => !tab?.title)
    ) {
      errorHandler(t("errors.tabContent")); // All tabs must have content and title
      return false;
    }
    if (
      config?.tabs?.some(
        (tab) => tab.content.replace(/<[^>]*>/g, "").trim().length < 3
      ) ||
      config?.tabs?.some((tab) => tab?.title?.length < 3)
    ) {
      errorHandler(t("errors.tabContentLength")); // Content and Title must be greater than 3 characters
      return false;
    }
    if (!config?.rows || !config?.columns) {
      errorHandler(t("errors.matrixRowsColumns")); // Rows and Columns are required
      return false;
    }
    if (config?.rows?.length < 2 || config?.columns?.length < 2) {
      errorHandler(t("errors.matrixRowsColumns")); // Atleast 2 rows and columns are required
      return false;
    }
    if (
      config?.rows?.some((r) => !r?.value) ||
      config?.columns?.some((c) => !c?.value)
    ) {
      errorHandler(t("errors.matrixRowColumnValue")); // All rows and columns must have values
      return false;
    }
    if (Object.keys(config?.matrix || {}).length === 0) {
      errorHandler(t("errors.matrixSelectionRequired")); // Atleast one selection is required
      return false;
    }
    return true;
  };

  const isFindingsValid = (config: FindingsConfiguration) => {
    if (
      (config?.tabs &&
        config?.tabs?.length > 0 &&
        config?.tabs?.some(
          (tab) => !tab?.content || !hasRealContent(tab.content)
        )) ||
      config?.tabs?.some((tab) => !tab?.title)
    ) {
      errorHandler(t("errors.tabContent")); // All tabs must have content and title
      return false;
    }
    if (
      config?.tabs?.some(
        (tab) => tab.content.replace(/<[^>]*>/g, "").trim().length < 3
      ) ||
      config?.tabs?.some((tab) => tab?.title?.length < 3)
    ) {
      errorHandler(t("errors.tabContentLength")); // Content and Title must be greater than 3 characters
      return false;
    }
    if (
      config?.findings
        ?.map((f) => f.value?.trim())
        // .filter(Boolean) // removes empty/whitespace-only values
        .join(",")
        .endsWith(",")
    ) {
      errorHandler(t("errors.findingsEndingWithComma")); // All findings must have content and should not include a comma at the end!
      return false;
    }
    if (
      !config?.findings ||
      config?.findings?.length < 2 ||
      config?.findings?.some((a) => !a?.value)
    ) {
      errorHandler(t("errors.findingsRequired")); // Atleast 2 findings are required
      return false;
    }
    if (
      !config?.correctFindings ||
      config?.correctFindings?.length < 1 ||
      config?.correctFindings?.some((a) => !a)
    ) {
      errorHandler(t("errors.correctFindingsRequired")); // Atleast 1 correct finding is required
      return false;
    }
    return true;
  };

  const isHighlightedTextValid = (config: HighlightedConfiguration) => {
    if (
      (config?.tabs &&
        config?.tabs?.length > 0 &&
        config?.tabs?.some(
          (tab) => !tab?.content || !hasRealContent(tab.content)
        )) ||
      config?.tabs?.some((tab) => !tab?.title)
    ) {
      errorHandler(t("errors.tabContent")); // All tabs must have content and title
      return false;
    }
    if (
      config?.tabs?.some(
        (tab) => tab.content.replace(/<[^>]*>/g, "").trim().length < 3
      ) ||
      config?.tabs?.some((tab) => tab?.title?.length < 3)
    ) {
      errorHandler(t("errors.tabContentLength")); // Content and Title must be greater than 3 characters
      return false;
    }

    if (
      !hasRealContent(config?.highlightedText) &&
      (!config?.tableData || config?.tableData?.length < 2)
    ) {
      errorHandler(t("errors.highlightedTextOrTableDataRequired")); // Highlighted text or table data is required
      return false;
    }

    // if highlighted text is provided
    if (hasRealContent(config?.highlightedText)) {
      if (
        !config?.highlightedText ||
        !hasRealContent(config?.highlightedText)
      ) {
        errorHandler(t("errors.highlightedTextRequired")); // Highlighted text is required
        return false;
      }
      if (
        !config?.highlightedPhrases ||
        config?.highlightedPhrases?.length < 1
      ) {
        errorHandler(t("errors.highlightedPhrasesRequired")); // Atleast 1 highlighted phrase is required
        return false;
      }
      if (config?.highlightedPhrases?.some((p) => !p?.text)) {
        errorHandler(t("errors.highlightedPhrasesTextRequired")); // All highlighted phrases must have text
        return false;
      }
      if (!config?.highlightedPhrases?.some((p) => p?.isCorrect)) {
        errorHandler(t("errors.highlightedPhrasesIsCorrectRequired")); // At least one highlighted phrase must be correct
        return false;
      }
    }

    // if table data is provided
    if (config?.tableData?.length > 0 && config?.tableHeaders?.length > 0) {
      if (!config?.tableData || config?.tableData?.length < 2) {
        errorHandler(t("errors.tableDataRequired")); // Atleast 2 rows and columns are required
        return false;
      }
      if (!config?.tableHeaders || config?.tableHeaders?.length < 2) {
        errorHandler(t("errors.tableHeadersRequired")); // Atleast 2 table headers are required
        return false;
      }
      if (
        config?.tableData?.some((row) => row?.some((cell) => cell?.length < 5))
      ) {
        errorHandler(t("errors.tableCellLength")); // Each cell in table data must be at least 5 characters long
        return false;
      }
      if (
        !config?.tableHighlightedPhrases ||
        config?.tableHighlightedPhrases?.length < 1
      ) {
        errorHandler(t("errors.tableHighlightedPhrasesRequired")); // Atleast 1 table highlighted phrase is required
        return false;
      }
      if (!config?.tableHighlightedPhrases?.some((p) => p?.isCorrect)) {
        errorHandler(t("errors.tableHighlightedPhrasesIsCorrectRequired")); // At least one table highlighted phrase must be correct
        return false;
      }
    }
    return true;
  };

  const isCaseStudyValid = (config: CaseStudyConfiguration) => {
    if (
      (config?.tabs &&
        config?.tabs?.length > 0 &&
        config?.tabs?.some(
          (tab) => !tab?.content || !hasRealContent(tab.content)
        )) ||
      config?.tabs?.some((tab) => !tab?.title)
    ) {
      errorHandler(t("errors.tabContent")); // All tabs must have content and title
      return false;
    }
    if (
      config?.tabs?.some(
        (tab) => tab.content.replace(/<[^>]*>/g, "").trim().length < 3
      ) ||
      config?.tabs?.some((tab) => tab?.title?.length < 3)
    ) {
      errorHandler(t("errors.tabContentLength")); // Content and Title must be greater than 3 characters
      return false;
    }
    if (
      config?.tabs &&
      config?.tabs?.length > 0 &&
      config?.tabs?.some((tab) => !tab?.displayFrom)
    ) {
      errorHandler(t("errors.displayFromRequired")); // Display from is required
      return false;
    }
    if (
      config?.tabs &&
      config?.tabs?.length > 0 &&
      config?.tabs?.some((tab) => !tab?.displayTill)
    ) {
      errorHandler(t("errors.displayTillRequired")); // Display till is required
      return false;
    }

    if (!config?.questions || config?.questions?.length <= 0) {
      errorHandler(t("errors.questionsRequired")); // Atleast 1 question is required
      return false;
    }

    const isQuestionsValid = config?.questions?.every((questionData) => {
      // Descriptive question text validation for all Questions
      if (!hasActualContent(questionData?.questionText)) {
        errorHandler(t("errors.descriptiveQuestionText"));
        return false;
      }

      switch (questionData?.questionType) {
        case QUESTION_TYPE.MCQ:
        case QUESTION_TYPE.PICTORIAL:
          return isMCQOrPictorialValid(questionData);
        // Descriptive question text validation is already dealt in validateCommonFields
        // case QUESTION_TYPE.DESCRIPTIVE:
        //   return isDescriptiveValid(
        //     questionData?.configuration as DescriptiveConfiguration
        //   );
        case QUESTION_TYPE.FILL_IN_THE_BLANK:
        case QUESTION_TYPE.FILL_IN_THE_BLANK_TEXTUAL:
          return isFillInTheBlankValid(
            questionData?.configuration as FillInTheBlankConfiguration
          );
        case QUESTION_TYPE.FILL_IN_THE_BLANK_FROM_LIST:
          return isFillInTheBlankFromListValid(
            questionData?.configuration as FillInTheBlankFromListConfiguration
          );
        case QUESTION_TYPE.BOW_TIE:
          return isBowTieValid(
            questionData?.configuration as BowTieConfiguration
          );
        case QUESTION_TYPE.FINDINGS:
          return isFindingsValid(
            questionData?.configuration as FindingsConfiguration
          );
        case QUESTION_TYPE.MATRIX:
          return isMatrixValid(
            questionData?.configuration as MatrixConfiguration
          );
        case QUESTION_TYPE.HIGHLIGHTED:
          return isHighlightedTextValid(
            questionData?.configuration as HighlightedConfiguration
          );
        case QUESTION_TYPE.CASE_STUDY:
          return isCaseStudyValid(
            questionData?.configuration as CaseStudyConfiguration
          );
        // case QUESTION_TYPE.WHITE_BOARD:
        //   return isWhiteBoardValid(
        //     questionData?.configuration as WhiteBoardConfiguration
        //   );
        default:
          return true;
      }
    });

    return isQuestionsValid;
  };

  const isQuestionDataValid = () => {
    if (!validateCommonFields()) {
      return false;
    }

    switch (questionData?.questionType) {
      case QUESTION_TYPE.MCQ:
      case QUESTION_TYPE.PICTORIAL:
        return isMCQOrPictorialValid(questionData);
      // Descriptive question text validation is already dealt in validateCommonFields
      // case QUESTION_TYPE.DESCRIPTIVE:
      //   return isDescriptiveValid(
      //     questionData?.configuration as DescriptiveConfiguration
      //   );
      case QUESTION_TYPE.FILL_IN_THE_BLANK:
      case QUESTION_TYPE.FILL_IN_THE_BLANK_TEXTUAL:
        return isFillInTheBlankValid(
          questionData?.configuration as FillInTheBlankConfiguration
        );
      case QUESTION_TYPE.FILL_IN_THE_BLANK_FROM_LIST:
        return isFillInTheBlankFromListValid(
          questionData?.configuration as FillInTheBlankFromListConfiguration
        );
      case QUESTION_TYPE.BOW_TIE:
        return isBowTieValid(
          questionData?.configuration as BowTieConfiguration
        );
      case QUESTION_TYPE.FINDINGS:
        return isFindingsValid(
          questionData?.configuration as FindingsConfiguration
        );
      case QUESTION_TYPE.MATRIX:
        return isMatrixValid(
          questionData?.configuration as MatrixConfiguration
        );
      case QUESTION_TYPE.HIGHLIGHTED:
        return isHighlightedTextValid(
          questionData?.configuration as HighlightedConfiguration
        );
      case QUESTION_TYPE.CASE_STUDY:
        return isCaseStudyValid(
          questionData?.configuration as CaseStudyConfiguration
        );
      // case QUESTION_TYPE.WHITE_BOARD:
      //   return isWhiteBoardValid(
      //     questionData?.configuration as WhiteBoardConfiguration
      //   );
      default:
        return true;
    }
  };

  const onSubmitQuestion = async () => {
    // Making decision to update or create question
    if (updateId) {
      await onUpdateQuestion();
    } else if (!updateId) {
      await onCreateQuestion();
    }
  };

  const onUpdateQuestion = async () => {
    try {
      // checking data validity
      if (isQuestionDataValid()) {
        setLoading(true);
        const response = await agent.Questions.updateQuestion(
          updateId,
          questionData
        );
        if (response?.code === 200) {
          trackAction("Question_Update", {
            questionId: updateId,
            question: questionData?.questionText,
            courseId: questionData?.courseId,
            gradeId: questionData?.gradeId,
          });
          successHandler(response);
          onCancel();
          setRefetch((prev) => !prev);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const onCreateQuestion = async () => {
    try {
      // checking data validity
      if (isQuestionDataValid()) {
        setLoading(true);
        const response = await agent.Questions.createQuestion(questionData);
        if (response?.code === 201) {
          trackAction("Question_Create", {
            question: questionData?.questionText,
            courseId: questionData?.courseId,
            gradeId: questionData?.gradeId,
          });
          successHandler(response);
          onCancel();
          setRefetch((prev) => !prev);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    resetQuestionData();
    navigateTo(PROTECTED_ROUTES.QUESTIONS_LISTING, { state: { subject } });
  };

  const contextValues = {
    loading,
    subject,
    onCancel,
    updateId,
    setSubject,
    questionData,
    editQuestion,
    addQuestionTags,
    setQuestionData,
    onSubmitQuestion,
    removeQuestionTag,
    resetQuestionData,
  };

  return (
    <QuestionContext.Provider value={contextValues}>
      {children}
    </QuestionContext.Provider>
  );
};

export { QuestionContext };
export default QuestionProvider;
